import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useTeam } from '@/hooks/useTeam'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import Textarea from '@/components/common/Textarea'
import Loading from '@/components/common/Loading'

/**
 * Strip HTML tags from text for clean comparison
 */
function stripHtmlForDiff(html) {
  if (!html) return ''
  return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
}

/**
 * Simple text diff component - shows what changed between old and new content
 * Uses a basic Longest Common Subsequence approach for better accuracy
 */
function SimpleDiff({ oldText, newText }) {
  if (!oldText || !newText) return null
  
  // Strip HTML before comparing
  const cleanOld = stripHtmlForDiff(oldText)
  const cleanNew = stripHtmlForDiff(newText)
  
  // Split into words
  const oldWords = cleanOld.split(/\s+/).filter(w => w.length > 0)
  const newWords = cleanNew.split(/\s+/).filter(w => w.length > 0)
  
  // Build word frequency maps for better comparison
  const oldFreq = {}
  const newFreq = {}
  
  oldWords.forEach(w => { oldFreq[w] = (oldFreq[w] || 0) + 1 })
  newWords.forEach(w => { newFreq[w] = (newFreq[w] || 0) + 1 })
  
  // Find truly removed words (in old but not in new, or fewer occurrences)
  const removed = []
  const added = []
  
  // Check for removed words
  for (const word of Object.keys(oldFreq)) {
    const diff = oldFreq[word] - (newFreq[word] || 0)
    if (diff > 0) {
      for (let i = 0; i < Math.min(diff, 3); i++) {
        removed.push(word)
      }
    }
  }
  
  // Check for added words  
  for (const word of Object.keys(newFreq)) {
    const diff = newFreq[word] - (oldFreq[word] || 0)
    if (diff > 0) {
      for (let i = 0; i < Math.min(diff, 3); i++) {
        added.push(word)
      }
    }
  }
  
  if (removed.length === 0 && added.length === 0) {
    return <p className="text-xs text-gray-500 italic">No text changes detected (formatting only)</p>
  }
  
  return (
    <div className="text-sm space-y-2 p-3 bg-gray-50 rounded border border-gray-200">
      {removed.length > 0 && (
        <div>
          <span className="text-xs font-medium text-red-700">Removed: </span>
          <span className="bg-red-100 text-red-800 px-1 rounded">
            {removed.slice(0, 15).join(' ')}{removed.length > 15 ? '...' : ''}
          </span>
        </div>
      )}
      {added.length > 0 && (
        <div>
          <span className="text-xs font-medium text-green-700">Added: </span>
          <span className="bg-green-100 text-green-800 px-1 rounded">
            {added.slice(0, 15).join(' ')}{added.length > 15 ? '...' : ''}
          </span>
        </div>
      )}
    </div>
  )
}

function WikiReview() {
  const location = useLocation()
  const navigate = useNavigate()
  const { team, loading: teamLoading } = useTeam()
  
  const [suggestions, setSuggestions] = useState([])
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [editingId, setEditingId] = useState(null)
  const [editData, setEditData] = useState({})
  const [saving, setSaving] = useState(false)
  const [sections, setSections] = useState([]) // Available sections from wiki_section_config
  const [existingItemsMap, setExistingItemsMap] = useState({}) // Map of target_id -> existing item data
  const [showDiff, setShowDiff] = useState({}) // Track which suggestions have diff view open
  const [dismissingId, setDismissingId] = useState(null) // Track which suggestion is being dismissed
  
  const DISMISS_REASONS = [
    { value: 'duplicate', label: 'Already exists in wiki' },
    { value: 'incorrect', label: 'Information is wrong' },
    { value: 'not_important', label: 'Not important enough' },
    { value: 'temporary', label: 'One-time info (not wiki-worthy)' },
    { value: 'other', label: 'Other reason' }
  ]

  useEffect(() => {
    if (!team?.id) return

    const fetchData = async () => {
      try {
        // Fetch wiki suggestions
        const { data: suggestionsData, error: suggestionsError } = await supabase
          .from('wiki_suggestions')
          .select('*')
          .eq('team_id', team.id)
          .eq('status', 'pending')
          .order('created_at', { ascending: false })

        if (suggestionsError) throw suggestionsError

        if (!suggestionsData || suggestionsData.length === 0) {
          // No suggestions, redirect to wiki manager
          navigate('/wiki')
          return
        }

        // Fetch existing sections
        const { data: sectionsData, error: sectionsError } = await supabase
          .from('wiki_section_config')
          .select('category_name, icon')
          .eq('team_id', team.id)
          .order('sort_order', { ascending: true })

        if (sectionsError) throw sectionsError

        // Add "Quick Notes" if not present
        const sectionNames = sectionsData?.map(s => s.category_name) || []
        if (!sectionNames.includes('Quick Notes')) {
          sectionNames.push('Quick Notes')
        }

        setSections(sectionsData || [])

        // Add tempId for tracking selections
        const suggestionsWithIds = suggestionsData.map(s => ({
          ...s,
          tempId: s.id // Use actual DB id
        }))

        setSuggestions(suggestionsWithIds)
        setSelectedIds(new Set(suggestionsWithIds.map(s => s.tempId)))
        
        // Fetch existing items for UPDATE suggestions (to show diff)
        const updateTargetIds = suggestionsData
          .filter(s => s.action === 'update' && s.target_id)
          .map(s => s.target_id)
        
        if (updateTargetIds.length > 0) {
          const { data: existingItems } = await supabase
            .from('wiki_items')
            .select('id, title, content, type, section')
            .in('id', updateTargetIds)
          
          if (existingItems) {
            const itemsMap = {}
            existingItems.forEach(item => {
              itemsMap[item.id] = item
            })
            setExistingItemsMap(itemsMap)
          }
        }
      } catch (err) {
        console.error('Failed to fetch data:', err)
        toast.error('Failed to load suggestions')
        navigate('/wiki')
      }
    }

    fetchData()
  }, [team?.id, navigate])

  const toggleSelection = (tempId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(tempId)) {
        newSet.delete(tempId)
      } else {
        newSet.add(tempId)
      }
      return newSet
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === suggestions.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(suggestions.map(s => s.tempId)))
    }
  }

  const handleEdit = (suggestion) => {
    setEditingId(suggestion.tempId)
    setEditData({
      title: suggestion.title,
      content: suggestion.content,
      section: suggestion.section // Use section consistently (base schema field)
    })
  }

  const handleSaveEdit = (tempId) => {
    setSuggestions(prev => prev.map(s => 
      s.tempId === tempId ? { ...s, ...editData } : s
    ))
    setEditingId(null)
    setEditData({})
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditData({})
  }

  const handleDismiss = async (suggestionId, reason) => {
    try {
      const { error } = await supabase
        .from('wiki_suggestions')
        .update({ 
          status: 'dismissed',
          reviewed_at: new Date().toISOString(),
          dismiss_reason: reason // Store reason for future analysis
        })
        .eq('id', suggestionId)
      
      if (error) throw error
      
      // Remove from local state
      setSuggestions(prev => prev.filter(s => s.id !== suggestionId))
      setSelectedIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(suggestionId)
        return newSet
      })
      setDismissingId(null)
      
      toast.success('Suggestion dismissed')
      
      // If no more suggestions, redirect
      if (suggestions.length <= 1) {
        navigate('/wiki')
      }
    } catch (err) {
      console.error('Failed to dismiss suggestion:', err)
      toast.error('Failed to dismiss suggestion')
    }
  }

  const handleAddSelected = async () => {
    if (selectedIds.size === 0) {
      toast.error('Please select at least one item to add')
      return
    }

    if (!team?.id) {
      toast.error('Team not found')
      return
    }

    setSaving(true)

    try {
      const selectedSuggestions = suggestions.filter(s => selectedIds.has(s.tempId))
      
      // Fetch existing wiki items
      const { data: existingItems, error: fetchError } = await supabase
        .from('wiki_items')
        .select('id, title, section, parent_id, parent_key')
        .eq('team_id', team.id)
      
      if (fetchError) throw fetchError
      
      let insertCount = 0
      let updateCount = 0
      
      // Process each suggestion
      for (const suggestion of selectedSuggestions) {
        // If this is an explicit UPDATE with target_id, update directly
        if (suggestion.action === 'update' && suggestion.target_id) {
          // Fetch existing item for change log
          const { data: existingItem } = await supabase
            .from('wiki_items')
            .select('*')
            .eq('id', suggestion.target_id)
            .single()
          
          const { error: updateError } = await supabase
            .from('wiki_items')
            .update({
              content: suggestion.content,
              type: suggestion.type || 'note',
              updated_at: new Date().toISOString()
            })
            .eq('id', suggestion.target_id)
          
          if (updateError) throw updateError
          
          // Log change for audit trail (manual approval)
          if (existingItem) {
            await supabase.from('wiki_change_log').insert({
              team_id: team.id,
              dispatch_id: suggestion.dispatch_id || null,
              wiki_item_id: suggestion.target_id,
              operation: 'update',
              previous_data: existingItem,
              new_data: { title: suggestion.title, content: suggestion.content, type: suggestion.type },
              confidence_score: suggestion.confidence_score,
              reasoning: 'Manually approved from review queue',
              auto_applied: false
            })
          }
          
          updateCount++
          continue
        }
        
        let parentId = null
        
        // For create_child, use target_id as parent
        if (suggestion.action === 'create_child' && suggestion.target_id) {
          parentId = suggestion.target_id
        }
        // Otherwise resolve parent via parent_title/parent_key
        else if (suggestion.parent_title || suggestion.parent_key) {
          // Prefer parent_key match
          let existingParent = null
          
          if (suggestion.parent_key) {
            existingParent = existingItems?.find(item => 
              item.section === suggestion.section &&
              item.parent_key === suggestion.parent_key &&
              !item.parent_id // Must be a parent row
            )
          }
          
          // Fallback: case-insensitive title match
          if (!existingParent && suggestion.parent_title) {
            existingParent = existingItems?.find(item =>
              item.section === suggestion.section &&
              !item.parent_id &&
              item.title.toLowerCase().includes(suggestion.parent_title.toLowerCase())
            )
          }
          
          if (existingParent) {
            parentId = existingParent.id
          } else {
            // Create parent item
            const { data: newParent, error: parentError } = await supabase
              .from('wiki_items')
              .insert({
                team_id: team.id,
                title: suggestion.parent_title || suggestion.parent_key,
                content: `All ${suggestion.parent_title || suggestion.parent_key} information`,
                type: 'note',
                section: suggestion.section || 'Quick Notes',
                category: suggestion.section || 'Quick Notes', // Write both for portal
                parent_key: suggestion.parent_key,
                source: 'ai_extracted',
                is_pinned: false,
                sort_order: 0
              })
              .select()
              .single()
            
            if (parentError) throw parentError
            parentId = newParent?.id
            insertCount++
          }
        }
        
        // STEP 2: Check if similar child exists under this parent (only if not explicit create)
        const similarChild = suggestion.action !== 'create' && suggestion.action !== 'create_child' 
          ? existingItems?.find(item =>
              item.parent_id === parentId &&
              item.section === suggestion.section &&
              (item.title.toLowerCase().includes(suggestion.title.toLowerCase().substring(0, 15)) ||
               suggestion.title.toLowerCase().includes(item.title.toLowerCase().substring(0, 15)))
            )
          : null
        
        if (similarChild) {
          // Fetch existing item for change log
          const { data: existingChild } = await supabase
            .from('wiki_items')
            .select('*')
            .eq('id', similarChild.id)
            .single()
          
          // UPDATE existing child
          const { error: updateError } = await supabase
            .from('wiki_items')
            .update({
              content: suggestion.content,
              type: suggestion.type || 'note',
              updated_at: new Date().toISOString()
            })
            .eq('id', similarChild.id)
          
          if (updateError) throw updateError
          
          // Log change for audit trail
          if (existingChild) {
            await supabase.from('wiki_change_log').insert({
              team_id: team.id,
              dispatch_id: suggestion.dispatch_id || null,
              wiki_item_id: similarChild.id,
              operation: 'update',
              previous_data: existingChild,
              new_data: { title: suggestion.title, content: suggestion.content, type: suggestion.type },
              confidence_score: suggestion.confidence_score,
              reasoning: 'Manually approved (matched existing item)',
              auto_applied: false
            })
          }
          
          updateCount++
        } else {
          // INSERT new child (or standalone item if no parent)
          const { data: newItem, error: insertError } = await supabase
            .from('wiki_items')
            .insert({
              team_id: team.id,
              title: suggestion.title,
              content: suggestion.content,
              type: suggestion.type || 'note',
              section: suggestion.section || 'Quick Notes',
              category: suggestion.section || 'Quick Notes', // Write both for portal
              parent_id: parentId,
              source: 'ai_extracted',
              is_pinned: false,
              sort_order: 0
            })
            .select()
            .single()
          
          if (insertError) throw insertError
          
          // Log change for audit trail
          if (newItem) {
            await supabase.from('wiki_change_log').insert({
              team_id: team.id,
              dispatch_id: suggestion.dispatch_id || null,
              wiki_item_id: newItem.id,
              operation: 'create',
              new_data: { title: suggestion.title, content: suggestion.content, type: suggestion.type, section: suggestion.section },
              confidence_score: suggestion.confidence_score,
              reasoning: 'Manually approved from review queue',
              auto_applied: false
            })
          }
          
          insertCount++
        }
      }
      
      // Mark suggestions as approved
      const { error: updateError } = await supabase
        .from('wiki_suggestions')
        .update({ 
          status: 'approved',
          reviewed_at: new Date().toISOString()
        })
        .in('id', selectedSuggestions.map(s => s.id))
      
      if (updateError) throw updateError
      
      // Dismiss unselected suggestions
      const unselectedIds = suggestions
        .filter(s => !selectedIds.has(s.tempId))
        .map(s => s.id)
      
      if (unselectedIds.length > 0) {
        await supabase
          .from('wiki_suggestions')
          .update({ 
            status: 'dismissed',
            reviewed_at: new Date().toISOString()
          })
          .in('id', unselectedIds)
      }
      
      // Success toast
      if (updateCount > 0 && insertCount > 0) {
        toast.success(`Updated ${updateCount} and added ${insertCount} items`)
      } else if (updateCount > 0) {
        toast.success(`Updated ${updateCount} ${updateCount === 1 ? 'item' : 'items'}`)
      } else {
        toast.success(`Added ${insertCount} ${insertCount === 1 ? 'item' : 'items'}`)
      }
      
      navigate('/wiki')
    } catch (err) {
      console.error('Error adding wiki items:', err)
      toast.error('Failed to add items to wiki')
    } finally {
      setSaving(false)
    }
  }

  if (teamLoading) {
    return <Loading text="Loading..." />
  }

  if (!team) {
    navigate('/team-setup')
    return null
  }

  if (suggestions.length === 0) {
    return null // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-brand-cream py-8">
      <Container>
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="secondary"
              className="text-sm mb-4"
            >
              ← Back to Dashboard
            </Button>
            
            <h1 className="font-heading text-3xl font-bold mb-2">
              Review Wiki Suggestions
            </h1>
            <p className="text-brand-slate">
              AI found {suggestions.length} {suggestions.length === 1 ? 'item' : 'items'} from your dispatch. 
              Select which ones to add to your team wiki.
            </p>
          </div>

          <Card>
            {/* Select All */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedIds.size === suggestions.length}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-border text-brand-green focus:ring-brand-green"
                />
                <span className="font-medium text-brand-charcoal">
                  Select All ({selectedIds.size} of {suggestions.length} selected)
                </span>
              </label>
            </div>

            {/* Suggestions List */}
            <div className="space-y-4">
              {suggestions.map((suggestion) => {
                const isSelected = selectedIds.has(suggestion.tempId)
                const isEditing = editingId === suggestion.tempId

                return (
                  <div
                    key={suggestion.tempId}
                    className={`p-4 border-2 rounded-lg transition-colors ${
                      isSelected ? 'border-brand-green bg-green-50' : 'border-border bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(suggestion.tempId)}
                        className="mt-1 w-5 h-5 rounded border-border text-brand-green focus:ring-brand-green"
                      />

                      {/* Content */}
                      <div className="flex-1">
                        {isEditing ? (
                          // Edit Mode
                          <div className="space-y-3">
                            <Input
                              label="Title"
                              value={editData.title}
                              onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                            />
                            <Textarea
                              label="Content"
                              value={editData.content}
                              onChange={(e) => setEditData({ ...editData, content: e.target.value })}
                              rows={3}
                            />
                            <div>
                              <label className="block text-sm font-medium text-brand-charcoal mb-1">
                                Section
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  list="section-list"
                                  value={editData.section}
                                  onChange={(e) => setEditData({ ...editData, section: e.target.value })}
                                  placeholder="Select or create section..."
                                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green"
                                />
                                <datalist id="section-list">
                                  {sections.map((section) => (
                                    <option key={section.category_name} value={section.category_name}>
                                      {section.icon} {section.category_name}
                                    </option>
                                  ))}
                                  <option value="Quick Notes">📄 Quick Notes</option>
                                </datalist>
                              </div>
                              <p className="text-xs text-brand-slate mt-1">
                                Choose an existing section or type a new one
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="primary"
                                onClick={() => handleSaveEdit(suggestion.tempId)}
                                className="text-sm"
                              >
                                Save
                              </Button>
                              <Button
                                variant="secondary"
                                onClick={handleCancelEdit}
                                className="text-sm"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <>
                            <div className="flex items-start justify-between gap-4 mb-2">
                              <h3 className="font-medium text-lg text-brand-charcoal">
                                {suggestion.title}
                              </h3>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="secondary"
                                  onClick={() => handleEdit(suggestion)}
                                  className="text-sm px-3 py-1"
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="secondary"
                                  onClick={() => setDismissingId(dismissingId === suggestion.id ? null : suggestion.id)}
                                  className="text-sm px-3 py-1 text-red-600 border-red-200 hover:bg-red-50"
                                >
                                  Dismiss
                                </Button>
                              </div>
                            </div>
                            
                            {/* Dismiss reason selector */}
                            {dismissingId === suggestion.id && (
                              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm font-medium text-red-900 mb-2">Why are you dismissing this?</p>
                                <div className="flex flex-wrap gap-2">
                                  {DISMISS_REASONS.map(reason => (
                                    <button
                                      key={reason.value}
                                      type="button"
                                      onClick={() => handleDismiss(suggestion.id, reason.value)}
                                      className="text-xs px-3 py-1.5 bg-white border border-red-200 text-red-700 rounded-full hover:bg-red-100 transition-colors"
                                    >
                                      {reason.label}
                                    </button>
                                  ))}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setDismissingId(null)}
                                  className="mt-2 text-xs text-red-600 hover:underline"
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                            
                            <p className="text-brand-slate mb-2 whitespace-pre-wrap">
                              {suggestion.content}
                            </p>
                            
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                                {sections.find(s => s.category_name === suggestion.section)?.icon || '📄'} {suggestion.section || 'Quick Notes'}
                              </span>
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                                {suggestion.type}
                              </span>
                              {suggestion.action && suggestion.action !== 'create' && (
                                <span className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded">
                                  {suggestion.action === 'update' ? '✏️ Update' : '📂 Add under parent'}
                                </span>
                              )}
                              {/* Color-coded confidence badge */}
                              {suggestion.confidence_score && (
                                <span className={`text-xs px-2 py-1 rounded ${
                                  suggestion.confidence_score >= 0.90 
                                    ? 'bg-green-100 text-green-700' 
                                    : suggestion.confidence_score >= 0.70 
                                    ? 'bg-yellow-100 text-yellow-700' 
                                    : 'bg-orange-100 text-orange-700'
                                }`}>
                                  {Math.round(suggestion.confidence_score * 100)}% confidence
                                </span>
                              )}
                              {/* Queue reason badge */}
                              {suggestion.queue_reason && (
                                <span className={`text-xs px-2 py-1 rounded ${
                                  suggestion.queue_reason === 'protected_item' 
                                    ? 'bg-purple-100 text-purple-700' 
                                    : suggestion.queue_reason === 'low_confidence'
                                    ? 'bg-orange-100 text-orange-700'
                                    : suggestion.queue_reason === 'auto_apply_disabled'
                                    ? 'bg-gray-100 text-gray-700'
                                    : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {suggestion.queue_reason === 'protected_item' && '🔒 Protected (manually edited)'}
                                  {suggestion.queue_reason === 'low_confidence' && '⚠️ Low confidence'}
                                  {suggestion.queue_reason === 'update_needs_review' && '👀 Update needs review'}
                                  {suggestion.queue_reason === 'auto_apply_disabled' && '⏸️ Auto-apply disabled'}
                                </span>
                              )}
                              {/* Show diff toggle for UPDATE operations */}
                              {suggestion.action === 'update' && suggestion.target_id && existingItemsMap[suggestion.target_id] && (
                                <button
                                  type="button"
                                  onClick={() => setShowDiff(prev => ({ 
                                    ...prev, 
                                    [suggestion.tempId]: !prev[suggestion.tempId] 
                                  }))}
                                  className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors"
                                >
                                  {showDiff[suggestion.tempId] ? '🔍 Hide Diff' : '🔍 Show Diff'}
                                </button>
                              )}
                            </div>
                            
                            {/* Diff view for UPDATE operations */}
                            {suggestion.action === 'update' && suggestion.target_id && showDiff[suggestion.tempId] && existingItemsMap[suggestion.target_id] && (
                              <div className="mt-3 space-y-2">
                                <div className="text-xs font-medium text-gray-600 mb-1">
                                  Comparing: "{existingItemsMap[suggestion.target_id].title}"
                                </div>
                                <div className="grid md:grid-cols-2 gap-3">
                                  <div className="p-2 bg-red-50 border border-red-200 rounded">
                                    <div className="text-xs font-medium text-red-700 mb-1">Current (will be replaced)</div>
                                    <p className="text-sm text-red-900 whitespace-pre-wrap">
                                      {existingItemsMap[suggestion.target_id].content?.substring(0, 300)}
                                      {existingItemsMap[suggestion.target_id].content?.length > 300 && '...'}
                                    </p>
                                  </div>
                                  <div className="p-2 bg-green-50 border border-green-200 rounded">
                                    <div className="text-xs font-medium text-green-700 mb-1">New (from AI)</div>
                                    <p className="text-sm text-green-900 whitespace-pre-wrap">
                                      {suggestion.content?.substring(0, 300)}
                                      {suggestion.content?.length > 300 && '...'}
                                    </p>
                                  </div>
                                </div>
                                <SimpleDiff 
                                  oldText={existingItemsMap[suggestion.target_id].content} 
                                  newText={suggestion.content} 
                                />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Actions */}
            <div className="mt-6 pt-4 border-t border-border flex gap-3">
              <Button
                variant="secondary"
                onClick={() => navigate('/wiki')}
                disabled={saving}
                className="flex-1"
              >
                Skip for Now
              </Button>
              <Button
                variant="primary"
                onClick={handleAddSelected}
                disabled={saving || selectedIds.size === 0}
                className="flex-1"
              >
                {saving ? 'Adding...' : `Add ${selectedIds.size} ${selectedIds.size === 1 ? 'Item' : 'Items'} to Wiki`}
              </Button>
            </div>
          </Card>
        </div>
      </Container>
    </div>
  )
}

export default WikiReview

